import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchIdentities,
  initiateLinking,
  setPrimaryIdentity,
  unlinkIdentity,
  openLinkingModal,
  closeLinkingModal,
} from '../store/slices/identitySlice';
import { redirectToLinkIdentity } from '../api/identity';
import { AUTH_URL } from '../api/client';

const providerIcons = {
  google: (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  ),
  salesforce: (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#00A1E0" d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.27 1.335-.42 2.07-.42 2.73 0 4.95 2.22 4.95 4.95s-2.22 4.95-4.95 4.95c-.39 0-.78-.045-1.14-.135a3.78 3.78 0 01-3.39 2.085c-.6 0-1.17-.135-1.68-.39a4.27 4.27 0 01-3.84 2.37c-1.995 0-3.72-1.365-4.2-3.3a4.245 4.245 0 01-3.375-4.155c0-2.325 1.89-4.215 4.215-4.215.195 0 .39.015.585.045a4.19 4.19 0 014.02-2.685z"/>
    </svg>
  ),
  manual: (
    <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
    </svg>
  ),
};

function IdentityManager() {
  const dispatch = useDispatch();
  const {
    identities,
    identitiesLoading,
    identitiesError,
    showLinkingModal,
    linkingUrl,
  } = useSelector((state) => state.identity);

  const [confirmUnlink, setConfirmUnlink] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    dispatch(fetchIdentities());
  }, [dispatch]);

  // Redirect when linking URL is ready
  useEffect(() => {
    if (linkingUrl) {
      // Build full URL
      const fullUrl = linkingUrl.startsWith('http') ? linkingUrl : `${AUTH_URL}${linkingUrl}`;
      redirectToLinkIdentity(fullUrl);
    }
  }, [linkingUrl]);

  const handleLinkAccount = (provider) => {
    setSelectedProvider(provider);
    dispatch(initiateLinking({
      provider,
      returnUrl: window.location.pathname,
    }));
  };

  const handleSetPrimary = (identityId) => {
    dispatch(setPrimaryIdentity({ identityId }));
  };

  const handleUnlink = (identityId) => {
    dispatch(unlinkIdentity({ identityId }));
    setConfirmUnlink(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // All providers are always available - users can link multiple accounts from the same provider
  const availableProviders = ['google', 'salesforce'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Linked Accounts</h3>
          <p className="text-sm text-gray-500">
            Manage the accounts linked to your profile. You can sign in with any of these accounts.
          </p>
        </div>
        {availableProviders.length > 0 && (
          <button
            onClick={() => dispatch(openLinkingModal())}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Link Account
          </button>
        )}
      </div>

      {/* Error message */}
      {identitiesError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {identitiesError}
        </div>
      )}

      {/* Identities list */}
      <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
        {identitiesLoading && identities.length === 0 ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : identities.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No linked accounts found.</div>
        ) : (
          identities.map((identity) => (
            <div
              key={identity.identity_id}
              className="p-4 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-4">
                {/* Provider icon */}
                <div className="flex-shrink-0">
                  {providerIcons[identity.provider] || providerIcons.manual}
                </div>

                {/* Email and details */}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{identity.email}</span>
                    {identity.is_primary && (
                      <span className="px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full">
                        Primary
                      </span>
                    )}
                    {identity.is_verified && (
                      <span className="text-green-500">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                    {/* Tenant association label */}
                    {identity.provider === 'salesforce' ? (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                        Salesforce login
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                        No tenant
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {identity.provider.charAt(0).toUpperCase() + identity.provider.slice(1)} &middot; Linked {formatDate(identity.linked_at)}
                    {identity.last_used_at && (
                      <> &middot; Last used {formatDate(identity.last_used_at)}</>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {!identity.is_primary && (
                  <>
                    <button
                      onClick={() => handleSetPrimary(identity.identity_id)}
                      disabled={identitiesLoading}
                      className="text-sm text-primary-600 hover:text-primary-800 disabled:opacity-50"
                    >
                      Make Primary
                    </button>
                    {confirmUnlink === identity.identity_id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUnlink(identity.identity_id)}
                          disabled={identitiesLoading}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmUnlink(null)}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmUnlink(identity.identity_id)}
                        disabled={identitiesLoading}
                        className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        Unlink
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Link Account Modal */}
      {showLinkingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Link Another Account</h3>
              <button
                onClick={() => dispatch(closeLinkingModal())}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-6">
              Choose a provider to link to your account. You&apos;ll be redirected to sign in.
            </p>

            <div className="space-y-3">
              {availableProviders.includes('google') && (
                <button
                  onClick={() => handleLinkAccount('google')}
                  disabled={identitiesLoading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                >
                  {providerIcons.google}
                  <span className="font-medium">Link Google Account</span>
                </button>
              )}
              {availableProviders.includes('salesforce') && (
                <button
                  onClick={() => handleLinkAccount('salesforce')}
                  disabled={identitiesLoading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                >
                  {providerIcons.salesforce}
                  <span className="font-medium">Link Salesforce Account</span>
                </button>
              )}
            </div>

            {identitiesLoading && (
              <div className="mt-4 text-center text-sm text-gray-500">
                Preparing to redirect...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default IdentityManager;
